import React, { useState, useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import { Space, Popconfirm, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { queryClient } from "../store/queryClient";
import { triggerNotification } from "../store/directActions";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import PageHeader from "../components/PageHeader";
import { onBarcodeScan } from "../utils/barcodeEvents";
import useSocket from "../hooks/useSocket";

interface OwnProps {}

type ShiftManagementPageProps = OwnProps & PropsFromRedux;

const fetchScanMetaApi = async (scanCode: string) => {
  return queryApi(`ledger/scan_reset/${scanCode}`, "GET");
};

function ShiftManagementPage({}: ShiftManagementPageProps) {
  const navigate = useNavigate();
  const { sendMessage } = useSocket();

  // Fetch scan meta on barcode scan, then reload the shift data
  const { mutate: fetchScanMeta } = useMutation({
    mutationFn: (code: string) => fetchScanMetaApi(code),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shift", "report"],
      });
      sendMessage("screen-refresh");
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
    },
  });

  // @ts-ignore
  useEffect(() => {
    return onBarcodeScan(async (data) => {
      // @ts-ignore
      if (data && data.trim()) fetchScanMeta(data);
    });
  }, []);

  // Mutation for marking ticket as sold
  const { mutate: markSold, isPending: isMarkingSold } = useMutation({
    mutationFn: (data: { scan_code: string; query: string }) =>
      queryApi(`ledger/scan?is_sold=1`, "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shift", "report"],
      });
      sendMessage("screen-refresh");
      triggerNotification({
        title: "Success",
        description: "Ticket marked as sold successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to mark ticket as sold",
        type: "error",
      });
    },
  });

  // Mutation for reverting sold ticket
  const { mutate: revertSold, isPending: isReverting } = useMutation({
    mutationFn: (data: { scan_code: string; query: string }) =>
      queryApi(`ledger/scan/sold?is_sold=0`, "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shift", "report"],
      });
      triggerNotification({
        title: "Success",
        description: "Ticket reverted successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to revert ticket",
        type: "error",
      });
    },
  });

  const onUpdateItem = (data: any) => {
    markSold({
      scan_code: data.activated_code,
      query: "is_sold=1",
    });
  };

  const onRevertItem = (data: any) => {
    revertSold({
      scan_code: data.activated_code,
      query: "is_sold=0",
    });
  };

  const computeShiftTotal = (record: any) => {
    const isReverse = record.is_reverse === 0 ? false : true;
    const sold = record.is_sold ? 1 : 0;
    return isReverse
      ? (record.start_no - record.current_no + sold) * record.lottery?.amount
      : (record.current_no - record.start_no + sold) * record.lottery?.amount;
  };

  const endShiftColumns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      render: (slot_number: number) => <div>#{slot_number}</div>,
      width: 70,
      sorter: (a: any, b: any) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) =>
        (a.lottery?.name ?? "").localeCompare(b.lottery?.name ?? ""),
      render: (_: any, record: any) => <div>{record.lottery?.name}</div>,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game",
      key: "game",
      sorter: (a: any, b: any) =>
        (a.lottery?.game_number ?? 0) - (b.lottery?.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (_: any, record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.lottery?.game_number}-{record.pack}
        </div>
      ),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Start #</div>,
      dataIndex: "start_no",
      key: "start_no",
      width: 100,
      sorter: (a: any, b: any) => (a.start_no ?? 0) - (b.start_no ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>End #</div>,
      dataIndex: "current_no",
      key: "current_no",
      width: 100,
      sorter: (a: any, b: any) => (a.current_no ?? 0) - (b.current_no ?? 0),
    },
    {
      title: "Value",
      dataIndex: "lottery",
      key: "lottery",
      sorter: (a: any, b: any) =>
        (a.lottery?.amount ?? 0) - (b.lottery?.amount ?? 0),
      render: (lottery: any) => (
        <div>
          {lottery?.currency}
          {lottery?.amount}
        </div>
      ),
      width: 100,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      sorter: (a: any, b: any) => computeShiftTotal(a) - computeShiftTotal(b),
      render: (_: any, record: any) => {
        const total = computeShiftTotal(record);
        return (
          <div>
            {record.lottery?.currency}
            {total}
          </div>
        );
      },
      width: 100,
    },
    {
      title: "Current Status",
      key: "is_sold",
      render: (_: any, record: any) => (
        <Space size="middle">
          {record.is_sold ? (
            <Popconfirm
              title="Are you sure to revert?"
              onConfirm={() => onRevertItem(record)}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
              disabled={isReverting}
            >
              <a href="#" style={{ color: "blue" }}>
                Revert Sold
              </a>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Are you sure to mark this sold?"
              onConfirm={() => onUpdateItem(record)}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
              disabled={isMarkingSold}
            >
              <a href="#" style={{ color: "red" }}>
                Mark Sold
              </a>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <section id="shift" className="page-section h-page p-6 flex flex-col">
      <PageHeader
        title="Manage Current Shift"
        extra={[
          <Button
            key="back"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>,
        ]}
      />

      <div className="bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-sm border app-border overflow-hidden flex flex-col flex-1 p-6">
        <h3 className="text-xl font-bold app-text mb-4">
          Sales by Game and Pack #
        </h3>
        {/* @ts-ignore */}
        <CustomPaginatedTable
          columns={endShiftColumns}
          hidePagination={true}
          filterData={{ type: 1 }}
          queryKey={["shift", "report", { type: 1 }]}
          path={"report/endshift"}
          tableProps={{
            sticky: true,
            tableLayout: "auto",
            scroll: { y: true, x: true },
            loading: isMarkingSold || isReverting,
            rowKey: (record: any) => record.slot_id || record.id,
          }}
        />
      </div>
    </section>
  );
}

const mapStateToProps = (state: RootState) => ({
  scan_meta: (state as any).inventory?.scan_meta,
});

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ShiftManagementPage);
